/**
 * Flattens note telemetry payloads for Statsig tracking
 * Converts nested objects (chars, timings, edit, metadata, interactionCounts)
 * into flat key-value pairs with string values for Statsig compatibility
 */

import type {
  EditEventMetrics,
  NoteViewedMetrics,
  SectionEventMetrics,
  SelectionMetrics,
} from './noteTelemetry';

/**
 * Flattened payload type for Statsig - all values must be strings
 */
export type FlattenedNoteTelemetryPayload = Record<string, string>;

/**
 * Convert a number to a string for Statsig
 */
function numToString(value: number): string {
  return String(value);
}

/**
 * Flatten SelectionMetrics payload
 * Used for: selection_update, text_selected, text_copied
 */
function flattenSelectionMetrics(payload: SelectionMetrics): FlattenedNoteTelemetryPayload {
  const flattened: FlattenedNoteTelemetryPayload = {
    selectionId: payload.selectionId,
    docId: payload.docId,
    docVersion: payload.docVersion,

    // Flatten chars object
    chars_selected: numToString(payload.chars.selected),
    chars_total: numToString(payload.chars.total),
    chars_percent: numToString(payload.chars.percent),

    // Flatten timings object
    timing_startedAt: numToString(payload.timings.startedAt),
    timing_lastChangedAt: numToString(payload.timings.lastChangedAt),
  };

  // Add optional timing fields
  if (payload.timings.settledAt !== undefined) {
    flattened.timing_settledAt = numToString(payload.timings.settledAt);
  }
  if (payload.timings.msToSettle !== undefined) {
    flattened.timing_msToSettle = numToString(payload.timings.msToSettle);
  }
  if (payload.timings.copiedAt !== undefined) {
    flattened.timing_copiedAt = numToString(payload.timings.copiedAt);
  }
  if (payload.timings.msToCopy !== undefined) {
    flattened.timing_msToCopy = numToString(payload.timings.msToCopy);
  }

  // Add optional top-level fields
  if (payload.modality) {
    flattened.modality = payload.modality;
  }
  if (payload.isBackward !== undefined) {
    flattened.isBackward = String(payload.isBackward);
  }
  if (payload.ua) {
    flattened.ua = payload.ua;
  }

  // Flatten metadata object
  if (payload.metadata) {
    if (payload.metadata.module_id) {
      flattened.metadata_module_id = payload.metadata.module_id;
    }
    if (payload.metadata.section_id) {
      flattened.metadata_section_id = payload.metadata.section_id;
    }
    if (payload.metadata.note_id) {
      flattened.metadata_note_id = payload.metadata.note_id;
    }
    if (payload.metadata.section_title) {
      flattened.metadata_section_title = payload.metadata.section_title;
    }
    if (payload.metadata.encounter_id) {
      flattened.metadata_encounter_id = payload.metadata.encounter_id;
    }
  }

  return flattened;
}

/**
 * Flatten NoteViewedMetrics payload
 * Used for: note_viewed
 */
function flattenNoteViewedMetrics(payload: NoteViewedMetrics): FlattenedNoteTelemetryPayload {
  const flattened: FlattenedNoteTelemetryPayload = {
    docId: payload.docId,
    docVersion: payload.docVersion,
  };

  // Flatten metadata object
  if (payload.metadata) {
    if (payload.metadata.module_id) {
      flattened.metadata_module_id = payload.metadata.module_id;
    }
    if (payload.metadata.section_id) {
      flattened.metadata_section_id = payload.metadata.section_id;
    }
    if (payload.metadata.note_id) {
      flattened.metadata_note_id = payload.metadata.note_id;
    }
    if (payload.metadata.section_title) {
      flattened.metadata_section_title = payload.metadata.section_title;
    }
    if (payload.metadata.encounter_id) {
      flattened.metadata_encounter_id = payload.metadata.encounter_id;
    }
  }

  return flattened;
}

/**
 * Flatten SectionEventMetrics payload
 * Used for: section_rendered, section_interacted
 */
function flattenSectionEventMetrics(payload: SectionEventMetrics): FlattenedNoteTelemetryPayload {
  const flattened: FlattenedNoteTelemetryPayload = {
    docId: payload.docId,
    docVersion: payload.docVersion,
  };

  // Add optional hover duration
  if (payload.hoverDuration !== undefined) {
    flattened.hoverDuration = numToString(payload.hoverDuration);
  }

  // Flatten interactionCounts object
  if (payload.interactionCounts) {
    if (payload.interactionCounts.clicks !== undefined) {
      flattened.interaction_clicks = numToString(payload.interactionCounts.clicks);
    }
    if (payload.interactionCounts.selects !== undefined) {
      flattened.interaction_selects = numToString(payload.interactionCounts.selects);
    }
    if (payload.interactionCounts.copies !== undefined) {
      flattened.interaction_copies = numToString(payload.interactionCounts.copies);
    }
  }

  // Flatten metadata object
  if (payload.metadata) {
    if (payload.metadata.module_id) {
      flattened.metadata_module_id = payload.metadata.module_id;
    }
    if (payload.metadata.section_id) {
      flattened.metadata_section_id = payload.metadata.section_id;
    }
    if (payload.metadata.note_id) {
      flattened.metadata_note_id = payload.metadata.note_id;
    }
    if (payload.metadata.section_title) {
      flattened.metadata_section_title = payload.metadata.section_title;
    }
    if (payload.metadata.encounter_id) {
      flattened.metadata_encounter_id = payload.metadata.encounter_id;
    }
  }

  return flattened;
}

/**
 * Flatten EditEventMetrics payload
 * Used for: section_edited
 */
function flattenEditEventMetrics(payload: EditEventMetrics): FlattenedNoteTelemetryPayload {
  const flattened: FlattenedNoteTelemetryPayload = {
    docId: payload.docId,
    docVersion: payload.docVersion,
    source: payload.source,

    // Flatten edit object
    edit_charsBefore: numToString(payload.edit.charsBefore),
    edit_charsAfter: numToString(payload.edit.charsAfter),
    edit_charsDelta: numToString(payload.edit.charsDelta),
    edit_percentDelta: numToString(payload.edit.percentDelta),

    // Flatten timings object (savedAt is always present)
    timing_savedAt: numToString(payload.timings.savedAt),
  };

  // Add optional timing fields
  if (payload.timings.startedAt !== undefined) {
    flattened.timing_startedAt = numToString(payload.timings.startedAt);
  }
  if (payload.timings.msInEdit !== undefined) {
    flattened.timing_msInEdit = numToString(payload.timings.msInEdit);
  }

  // Flatten metadata object
  if (payload.metadata) {
    if (payload.metadata.module_id) {
      flattened.metadata_module_id = payload.metadata.module_id;
    }
    if (payload.metadata.section_id) {
      flattened.metadata_section_id = payload.metadata.section_id;
    }
    if (payload.metadata.note_id) {
      flattened.metadata_note_id = payload.metadata.note_id;
    }
    if (payload.metadata.section_title) {
      flattened.metadata_section_title = payload.metadata.section_title;
    }
    if (payload.metadata.encounter_id) {
      flattened.metadata_encounter_id = payload.metadata.encounter_id;
    }
  }

  return flattened;
}

/**
 * Main flattening function - determines payload type and flattens accordingly
 */
export function flattenNoteTelemetryPayload(
  payload: SelectionMetrics | NoteViewedMetrics | SectionEventMetrics | EditEventMetrics,
): FlattenedNoteTelemetryPayload {
  // Type detection based on presence of discriminating fields

  // SelectionMetrics has 'chars' and 'timings.startedAt'
  if ('chars' in payload && 'timings' in payload && 'startedAt' in payload.timings) {
    return flattenSelectionMetrics(payload);
  }

  // EditEventMetrics has 'edit' and 'source'
  if ('edit' in payload && 'source' in payload) {
    return flattenEditEventMetrics(payload);
  }

  // SectionEventMetrics has either 'hoverDuration' or 'interactionCounts'
  if ('hoverDuration' in payload || 'interactionCounts' in payload) {
    return flattenSectionEventMetrics(payload);
  }

  // NoteViewedMetrics is the simplest - just docId, docVersion, metadata
  return flattenNoteViewedMetrics(payload as NoteViewedMetrics);
}
